import { Stack, Select, Group, Button } from "@mantine/core";
import { IconMail } from "@tabler/icons-react";
import { EmailTemplatesManager } from "./EmailTemplatesManager";
import { autoSendOptions, languageOptions } from "../utils/consts";
import type { AutoEmailSendingMode } from "../utils/types";

interface EmailGeneralSettingsSubTabProps {
  organization: any;
  autoSendMode: AutoEmailSendingMode | null;
  setAutoSendMode: (mode: AutoEmailSendingMode | null) => void;
  isUpdatingAutoEmailMode: boolean;
  handleSaveAutoSendMode: () => void;
  selectedLanguage: string | null;
  setSelectedLanguage: (lang: string | null) => void;
  handleUpdateAllCustomersLanguage: () => void;
  updateAllCustomersLanguageMutation: any;
  defaultLanguageQuery: any;
}

export function EmailGeneralSettingsSubTab({
  organization,
  autoSendMode,
  setAutoSendMode,
  isUpdatingAutoEmailMode,
  handleSaveAutoSendMode,
  selectedLanguage,
  setSelectedLanguage,
  handleUpdateAllCustomersLanguage,
  updateAllCustomersLanguageMutation,
  defaultLanguageQuery,
}: EmailGeneralSettingsSubTabProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-linear-to-br from-indigo-500 to-purple-500 text-white shadow-md">
          <IconMail size={24} stroke={2} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Email Settings</h2>
          <p className="text-sm text-gray-600">
            Control email preferences and default language
          </p>
        </div>
      </div>
      <div className="rounded-xl border border-indigo-100 bg-linear-to-br from-indigo-50/50 to-purple-50/30 p-6">
        <Stack gap="md">
          <Select
            label="Sending Mode"
            placeholder="Choose how drafts are sent"
            data={autoSendOptions}
            value={autoSendMode}
            onChange={(value) =>
              setAutoSendMode((value as AutoEmailSendingMode) ?? null)
            }
            allowDeselect={false}
            disabled={!organization || isUpdatingAutoEmailMode}
            comboboxProps={{ withinPortal: true }}
            classNames={{
              label: "font-semibold text-gray-700 mb-2",
            }}
          />
          <Group justify="flex-end" mt="md">
            <Button
              onClick={handleSaveAutoSendMode}
              disabled={
                !organization ||
                !autoSendMode ||
                autoSendMode === organization.auto_email_sending_mode ||
                isUpdatingAutoEmailMode
              }
              loading={isUpdatingAutoEmailMode}
              color="indigo"
              size="md"
              className="shadow-md hover:shadow-lg transition-shadow"
            >
              Save Preference
            </Button>
          </Group>
        </Stack>
      </div>
      <div className="rounded-xl border border-indigo-100 bg-linear-to-br from-indigo-50/50 to-purple-50/30 p-6">
        <Stack gap="md">
          <Select
            label="Default Language for All Customers"
            placeholder="Select default language"
            description="This will update the language for all customers in your organization. The current default is determined by the most common language among your customers."
            data={languageOptions}
            value={selectedLanguage}
            onChange={(value) => setSelectedLanguage(value ?? null)}
            allowDeselect={false}
            disabled={
              !organization ||
              updateAllCustomersLanguageMutation.isPending ||
              defaultLanguageQuery.isLoading
            }
            comboboxProps={{ withinPortal: true }}
            classNames={{
              label: "font-semibold text-gray-700 mb-2",
              description: "text-sm text-gray-600 mt-1",
            }}
          />
          <Group justify="flex-end" mt="md">
            <Button
              onClick={handleUpdateAllCustomersLanguage}
              disabled={
                !organization ||
                !selectedLanguage ||
                selectedLanguage === defaultLanguageQuery.data?.language ||
                updateAllCustomersLanguageMutation.isPending
              }
              loading={updateAllCustomersLanguageMutation.isPending}
              color="indigo"
              size="md"
              className="shadow-md hover:shadow-lg transition-shadow"
            >
              Update All Customers
            </Button>
          </Group>
        </Stack>
      </div>
      <div className="rounded-xl border border-indigo-100 bg-linear-to-br from-indigo-50/50 to-purple-50/30 p-6">
        <EmailTemplatesManager />
      </div>
    </div>
  );
}
